import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { LiveFrame } from '../components/junctions/LiveFrame';
import baseapi, { API_BASE } from '../baseapi';
import { useTheme } from '../context/ThemeContext';

/**
 * Queue cameras (16 Oct 2026, operators; first called "Antharalayam Entry cameras"): the four Antharalayam entry
 * line cameras live in one row, each with what it counts, and the entry lines added up at the top.
 * Counts are people crossing the camera's counting line in its own direction: "in" on an entry line, "out" at an
 * exit (the other way is people stepping back, not counted here). "Today" runs from the daily reset.
 * Two bar graphs (16 Oct 2026, operators: "which queue is moving faster"): people entering per minute on each line
 * over the last 5 / 15 / 60 minutes, fastest first, and every 5 minutes today - from each camera's own checkpoint
 * (GET /api/checkpoints/{cp}/series, one point a minute).
 */
type Dir = 'toward' | 'away';
interface Cam { id: string; name: string; short: string; cp: string; color: string; ip: string; make: string; dir: Dir; kind: 'entry' | 'exit' }

// the entry lines only, in the order the operators listed them (16 Oct 2026: the two exit cameras, CAM-2-44 and
// CAM-2-125, were taken off this page)
const CAMS: Cam[] = [
  { id: 'CAM-3-94', name: 'Temple Antharalayam Entry 100 Rupees Lines', short: '100 Rs', cp: 'CP-TEMPLE-ANTARALAYAM-ENTRY-3', color: '#E6781B', ip: '192.168.3.94', make: 'CP_PLUS', dir: 'toward', kind: 'entry' },
  { id: 'CAM-3-95', name: 'Temple Antharalayam Entry 500 Rupees Line', short: '500 Rs', cp: 'CP-TEMPLE-ANTARALAYAM-ENTRY-2', color: '#7C3AED', ip: '192.168.3.95', make: 'CP_PLUS', dir: 'toward', kind: 'entry' },
  { id: 'CAM-3-96', name: 'Temple Antharalayam Free Line Entry', short: 'Free line', cp: 'CP-VIEW-2-190', color: '#059669', ip: '192.168.3.96', make: 'CP_PLUS', dir: 'toward', kind: 'entry' },
  { id: 'CAM-3-98', name: 'Temple Antharalayam Entry 300 Rupees Ticket', short: '300 Rs', cp: 'CP-ANTHARALAYAM-ENTRY', color: '#2563EB', ip: '192.168.3.98', make: 'CP_PLUS', dir: 'toward', kind: 'entry' },
];
// the charts list the lines by price, the free line last
const CHART_ORDER = ['CAM-3-94', 'CAM-3-98', 'CAM-3-95', 'CAM-3-96'];
const WINDOWS = [{ label: '5 min', minutes: 5 }, { label: '15 min', minutes: 15 }, { label: '1 hour', minutes: 60 }];
type Point = { t: number; toward: number; away: number };
const hm = (t: number) => new Date(t * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });

// hover box of the 5-minute chart, laid out like the darshan chart's: the time, each line, then the overall total
function FiveMinTip({ active, payload }: any) {
  const row = active && payload?.[0]?.payload;
  if (!row) return null;
  return (
    <div className="min-w-[200px] rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#2b3140] shadow-lg px-3.5 py-2.5 text-sm">
      <div className="font-black text-neutral-900 dark:text-neutral-100 pb-1.5 mb-1.5 border-b border-neutral-200 dark:border-[#2b3140]">
        {hm(row.t)}–{hm(row.t + 300)}
      </div>
      {CHART_ORDER.map(id => CAMS.find(c => c.id === id)!).map(c => (
        <div key={c.id} className="flex items-center justify-between gap-6 py-0.5">
          <span className="flex items-center gap-2 font-semibold" style={{ color: c.color }}>
            <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: c.color }} />{c.short}
          </span>
          <span className="font-black text-neutral-900 dark:text-neutral-100 tabular-nums">{n(row[c.short])}</span>
        </div>
      ))}
      <div className="flex items-center justify-between gap-6 pt-1.5 mt-1.5 border-t border-neutral-200 dark:border-[#2b3140]">
        <span className="font-black text-neutral-900 dark:text-neutral-100">Overall Total</span>
        <span className="font-black text-neutral-900 dark:text-neutral-100 tabular-nums">{n(row.total)}</span>
      </div>
    </div>
  );
}

interface Counts { m5: number; m15: number; total: number }
const n = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('en-IN'));

export const AntharalayamCamerasPage: React.FC = () => {
  const [counts, setCounts] = useState<Record<string, Counts>>({});
  const [status, setStatus] = useState<Record<string, any>>({});

  // line counts of all six cameras: one /api/summary every 5 s
  useEffect(() => {
    let alive = true;
    const load = () => baseapi.get('summary').then(r => {
      if (!alive) return;
      const out: Record<string, Counts> = {};
      for (const cp of Object.values<any>(r.data?.checkpoints ?? {})) {
        for (const ln of Object.values<any>(cp?.lines ?? {})) {
          const cam = CAMS.find(c => c.id === ln?.camera_id);
          if (!cam) continue;
          const p = ln?.people?.[cam.dir] ?? {};
          const acc = out[cam.id] ?? (out[cam.id] = { m5: 0, m15: 0, total: 0 });
          acc.m5 += p.m5 ?? 0; acc.m15 += p.m15 ?? 0; acc.total += p.total ?? 0;
        }
      }
      setCounts(out);
    }).catch(() => { /* keep the last */ });
    load();
    const t = window.setInterval(() => { if (!document.hidden) load(); }, 5000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  // people in view and online / offline: each camera's status every 3 s
  useEffect(() => {
    let alive = true;
    const load = () => CAMS.forEach(c => baseapi.get(`cameras/${encodeURIComponent(c.id)}/status`)
      .then(r => { if (alive) setStatus(s => ({ ...s, [c.id]: r.data })); }).catch(() => { /* keep the last */ }));
    load();
    const t = window.setInterval(() => { if (!document.hidden) load(); }, 3000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  // people per minute on each line today (one point a minute): every 30 s
  const [series, setSeries] = useState<Record<string, Point[]>>({});
  const [win, setWin] = useState(15);
  useEffect(() => {
    let alive = true;
    const load = () => Promise.all(CAMS.map(c => baseapi.get(`checkpoints/${encodeURIComponent(c.cp)}/series?minutes=1440`)
      .then(r => [c.id, (r.data?.points ?? []) as Point[]] as const).catch(() => null)))
      .then(rows => {
        if (!alive) return;
        setSeries(s => { const next = { ...s }; for (const r of rows) if (r) next[r[0]] = r[1]; return next; });
      });
    load();
    const t = window.setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  // which queue is moving faster: people entering per minute over the chosen window, fastest first
  const speed = useMemo(() => {
    const now = Date.now() / 1000;
    return CAMS.filter(c => series[c.id]).map(c => {
      const n_ = series[c.id].filter(p => p.t >= now - win * 60).reduce((a, p) => a + (p[c.dir] || 0), 0);
      return { name: c.short, color: c.color, perMin: Math.round(n_ / win) };     // whole people (operators, 16 Oct 2026)
    }).sort((a, b) => b.perMin - a.perMin);
  }, [series, win]);

  // every 5 minutes today, the four lines side by side
  const fiveMin = useMemo(() => {
    const all = CAMS.flatMap(c => (series[c.id] ?? []).map(p => p.t));
    if (!all.length) return [];
    const first = Math.floor(Math.min(...all) / 300) * 300, last = Math.floor(Date.now() / 1000 / 300) * 300;
    const rows: Record<string, number | string>[] = [];
    for (let t = first; t <= last; t += 300) rows.push({ t, time: hm(t), ...Object.fromEntries(CAMS.map(c => [c.short, 0])) });
    for (const c of CAMS) for (const p of series[c.id] ?? []) {
      const row = rows[Math.floor((p.t - first) / 300)];
      if (row) row[c.short] = (row[c.short] as number) + (p[c.dir] || 0);
    }
    // the total of the four lines, written above the tallest bar of each 5 minutes (like the darshan chart)
    for (const row of rows) {
      row.total = CAMS.reduce((a, c) => a + (row[c.short] as number), 0);
      const top = CAMS.reduce((best, c) => ((row[c.short] as number) > (row[best] as number) ? c.short : best), CAMS[0].short);
      // the label reads its own row's total from "<line>#total" (recharts drops empty bars before numbering the
      // labels, so a label's index can point at another 5 minutes)
      for (const c of CAMS) row[`${c.short}#total`] = c.short === top && row.total ? row.total : '';
    }
    return rows;
  }, [series]);
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {                                     // open at the latest time, unless the user scrolled back
    const el = scroller.current;
    if (el && el.scrollWidth - el.scrollLeft - el.clientWidth < 120) el.scrollLeft = el.scrollWidth;
  }, [fiveMin.length]);
  const axis = { fontSize: 12, fill: '#737373' };
  const { theme } = useTheme();
  const totalFill = theme === 'dark' ? '#e5e5e5' : '#262626';

  const have = CAMS.every(c => counts[c.id]);
  const sum = (kind: Cam['kind'], k: keyof Counts) => (have ? CAMS.filter(c => c.kind === kind).reduce((a, c) => a + counts[c.id][k], 0) : null);
  const card = 'rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs';
  const summary: [string, number | null, number | null, string][] = [
    ['Entered (4 entry lines)', sum('entry', 'total'), sum('entry', 'm15'), 'text-emerald-700 dark:text-emerald-400'],
  ];

  return (
    <div className="space-y-3">
      <div className={`${card} p-3.5 flex flex-col lg:flex-row lg:items-center justify-between gap-3`}>
        <h1 className="text-lg font-black text-neutral-900 dark:text-neutral-100">Queue cameras</h1>
        <div className="grid grid-cols-1 gap-2">
          {summary.map(([label, today, m15, cls]) => (
            <div key={label} className="px-3 py-2 rounded-lg bg-neutral-50 dark:bg-[#14161e] border border-neutral-200 dark:border-[#262c39] min-w-[180px]">
              <div className="text-[11px] font-bold text-neutral-600 dark:text-neutral-400">{label}</div>
              <div className="flex items-baseline gap-1.5">
                <span className={`text-2xl font-black tabular-nums ${cls}`}>{n(today)}</span>
                <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Today</span>
              </div>
              <div className="text-xs font-semibold text-neutral-600 dark:text-neutral-400 tabular-nums">Last 15 min: {n(m15)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* the four cameras in one row on a desktop screen (16 Oct 2026, operators: all four visible at once) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
        {CAMS.map(c => {
          const st = status[c.id];
          const online = st?.state === 'online';
          const ct = counts[c.id];
          const cells: [string, string][] = [
            ['In view now', online && st?.tracks != null ? n(st.tracks) : '—'],
            ['Last 5 min', n(ct?.m5)],
            ['Last 15 min', n(ct?.m15)],
            ['Today', n(ct?.total)],
          ];
          return (
            <div key={c.id} className={`${card} overflow-hidden flex flex-col`}>
              <div className="px-2.5 py-1.5 border-b border-neutral-200 dark:border-[#262c39] flex items-start gap-2" title={c.name}>
                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${!st ? 'bg-neutral-300' : online ? 'bg-emerald-500' : 'bg-rose-500'}`}
                  title={st ? st.state : 'checking'} />
                <div className="min-w-0">
                  <div className="text-[13px] font-black text-neutral-900 dark:text-neutral-100 leading-snug truncate">{c.name}</div>
                  <div className="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 truncate">{c.id} · {c.ip} · {c.make}</div>
                </div>
              </div>
              <div className="aspect-video bg-black">
                <LiveFrame src={`${API_BASE}cameras/${encodeURIComponent(c.id)}/snapshot.jpg`} alt={c.name}
                  size="tile" maxFps={4} className="w-full h-full object-contain" />
              </div>
              <div className="grid grid-cols-4 divide-x divide-neutral-200 dark:divide-[#262c39] border-t border-neutral-200 dark:border-[#262c39]">
                {cells.map(([label, v]) => (
                  <div key={label} className="px-1 py-1.5 text-center">
                    <div className="text-[10px] font-bold text-neutral-600 dark:text-neutral-400 leading-tight whitespace-nowrap">{label}</div>
                    <div className="text-base font-black text-neutral-900 dark:text-neutral-100 tabular-nums leading-tight">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* which queue is moving faster */}
      <div className={`${card} p-3.5`}>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h2 className="text-base font-black text-neutral-900 dark:text-neutral-100">Queue Movement</h2>
          <div className="flex gap-1 p-1 rounded-lg bg-neutral-100 dark:bg-[#13151c] border border-neutral-200 dark:border-[#262c39]">
            {WINDOWS.map(w => (
              <button key={w.minutes} onClick={() => setWin(w.minutes)}
                className={`px-3 py-1 text-sm font-bold rounded-md cursor-pointer ${win === w.minutes ? 'bg-white dark:bg-[#252a3a] text-[#761D20] dark:text-orange-400 shadow-sm' : 'text-neutral-600 dark:text-neutral-400'}`}>
                {w.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ height: 210 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={speed} layout="vertical" margin={{ top: 4, right: 90, bottom: 4, left: 16 }}>
              <CartesianGrid horizontal={false} stroke="rgba(128,128,128,0.18)" />
              <XAxis type="number" tick={axis} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ ...axis, fontWeight: 700 }} width={96} />
              <Tooltip formatter={(v: any) => [`${v} per min`, 'Entering']} cursor={{ fill: 'rgba(128,128,128,0.08)' }} />
              <Bar dataKey="perMin" radius={[0, 4, 4, 0]} isAnimationActive={false}
                shape={(p: any) => <rect x={p.x} y={p.y} width={Math.max(0, p.width)} height={p.height} rx={4} fill={p.payload.color} />}>
                <LabelList dataKey="perMin" content={(p: any) => (            // in the bar's own colour: readable in both themes
                  <text x={Number(p.x) + Number(p.width) + 6} y={Number(p.y) + Number(p.height) / 2} dy={4}
                    fontSize={13} fontWeight={800} fill={speed[p.index]?.color}>{p.value} per min</text>)} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* every 5 minutes today */}
      <div className={`${card} p-3.5`}>
        <h2 className="text-base font-black text-neutral-900 dark:text-neutral-100 mb-2">Devotee Entries Every 5 Minutes — Today</h2>
        <div ref={scroller} className="overflow-x-auto">
          <div style={{ height: 300, minWidth: Math.max(600, fiveMin.length * 48) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={fiveMin} margin={{ top: 24, right: 8, bottom: 4, left: 0 }} barCategoryGap="18%">
                <CartesianGrid vertical={false} stroke="rgba(128,128,128,0.18)" />
                <XAxis dataKey="time" tick={axis} interval="preserveStartEnd" minTickGap={24} />
                <YAxis tick={axis} allowDecimals={false} width={44} />
                <Tooltip content={<FiveMinTip />} cursor={{ fill: 'rgba(128,128,128,0.08)' }} />
                <Legend wrapperStyle={{ fontSize: 13, fontWeight: 700 }} />
                {CHART_ORDER.map(id => CAMS.find(c => c.id === id)!).map(c => (
                  <Bar key={c.id} dataKey={c.short} fill={c.color} isAnimationActive={false}>
                    <LabelList dataKey={`${c.short}#total`} content={(p: any) => (p.value === '' || p.value == null ? null :
                      <text x={Number(p.x) + Number(p.width) / 2} y={Number(p.y) - 6} textAnchor="middle"
                        fontSize={12} fontWeight={800} fill={totalFill}>{n(Number(p.value))}</text>)} />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AntharalayamCamerasPage;
